import { env } from '@/env'

const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_API_KEY,
  projectId: env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.REACT_APP_FIREBASE_APP_ID,
}

const isConfigured = () =>
  Boolean(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId &&
    env.REACT_APP_FIREBASE_VAPID_KEY,
  )

const registerServiceWorker = (sdkVersion: string) => {
  const params = new URLSearchParams({
    ...(firebaseConfig as Record<string, string>),
    v: sdkVersion,
  })
  return navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${params.toString()}`,
  )
}

const mintToken = async (): Promise<string | null> => {
  const { SDK_VERSION, getApps, initializeApp } = await import('@firebase/app')
  const { getMessaging, getToken, isSupported } =
    await import('@firebase/messaging')

  if (!(await isSupported())) {
    return null
  }

  const app = getApps()[0] ?? initializeApp(firebaseConfig)
  const serviceWorkerRegistration = await registerServiceWorker(SDK_VERSION)

  return getToken(getMessaging(app), {
    vapidKey: env.REACT_APP_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration,
  })
}

/*
 * One mint at a time. `getToken` re-subscribes the PushManager, so overlapping
 * calls each produce a token and the earlier one is left addressing a
 * subscription the later one replaced. FCM accepts a send to that token and the
 * browser displays nothing, reporting UNREGISTERED only once the push service
 * answers 410 — a silent failure with no error on either side.
 */
let pendingToken: Promise<string | null> | null = null

interface GetWebPushTokenOptions {
  requestPermission?: boolean
}

export const getWebPushToken = async ({
  requestPermission = false,
}: GetWebPushTokenOptions = {}): Promise<string | null> => {
  if (!isConfigured()) {
    return null
  }

  /*
   * Asked for before any await: transient user activation lasts seconds, and
   * the dynamic imports below can outlive it, after which the browser
   * suppresses the prompt silently. These checks are the synchronous subset of
   * `isSupported()`, which still runs before anything is registered.
   */
  if (
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return null
  }
  if (Notification.permission === 'default' && requestPermission) {
    await Notification.requestPermission()
  }
  if (Notification.permission !== 'granted') {
    return null
  }

  pendingToken ??= mintToken().finally(() => {
    pendingToken = null
  })

  return pendingToken
}
